import type {
    Oauth2ExchangeslistexchangesResponses, TypeEnum2,
} from "../../generated";
import type { BluvoClient } from "../BluvoClient";
import { BluvoWebClient } from "../BluvoWebClient";
import {
	type BluvoError,
	ERROR_CODES,
	extractErrorCode,
	extractErrorResult,
	extractErrorTypeInfo,
	WITHDRAWAL_QUOTATION_ERROR_TYPES,
} from "../error-codes";
import type { FlowActionType, FlowState } from "../types/flow.types";
import type { Machine } from "../types/machine.types";
import type { Subscription } from "../WebSocketClient";
import {
	type WithdrawFundsWorkflowMessageBody,
	type WorkflowMessageBody,
	WorkflowTypes,
} from "../WorkflowTypes";
import { createFlowMachine } from "./flowMachine";
import { transformBalances } from "../utils/balanceTransform";
import { WithdrawalErrorHandler } from "./withdrawalErrorHandler";
import { BluvoCache, type BluvoCacheOptions } from "../BluvoCache";
import type { QRCodeAuthWorkflowMessageBody } from "../WorkflowTypes";

// Exchanges that use QR code authentication instead of OAuth popup
const QR_CODE_EXCHANGES = ['binance-web', 'bybit-web'];

// Default QR code timeout in milliseconds (5 minutes)
const DEFAULT_QRCODE_TIMEOUT_MS = 5 * 60 * 1000;

export interface BluvoFlowClientOptions {
	orgId: string;
	projectId: string;
	listExchangesFn: BluvoClient["oauth2"]["listExchanges"];
	fetchWithdrawableBalanceFn: BluvoClient["wallet"]["withdrawals"]["getWithdrawableBalance"];
	requestQuotationFn: BluvoClient["wallet"]["withdrawals"]["requestQuotation"];
	executeWithdrawalFn: BluvoClient["wallet"]["withdrawals"]["executeWithdrawal"];
	getWalletByIdFn: BluvoClient["wallet"]["get"];
	pingWalletByIdFn: BluvoClient["wallet"]["ping"];
	mkUUIDFn?: () => string;
	onWalletConnectedFn?: (walletId: string, exchange: string) => any;

	options?: {
		sandbox?: boolean;
		dev?: boolean;
		maxRetryAttempts?: number;
		autoRefreshQuotation?: boolean;
		customDomain?:
			| string
			| "api-bluvo.com"
			| {
					api: string;
					ws: string;
			  };
	};

	/**
	 * Cache configuration for persisting QR codes across page reloads.
	 * By default uses localStorage with prefix "bluvo:" and 15-second safety threshold.
	 * Pass `{ disabled: true }` to disable caching entirely, or provide a custom adapter.
	 */
	cache?: BluvoCacheOptions;
}

export interface WithdrawalFlowOptions {
	exchange: string;
	walletId: string;
	popupOptions?: {
		title?: string;
		width?: number;
		height?: number;
		left?: number;
		top?: number;
	};
}

export interface ResumeWithdrawalFlowOptions {
	exchange: string;
	walletId: string;
}

/**
 * Options for silently resuming a withdrawal flow with preloaded balance data
 *
 * This interface allows you to resume a withdrawal flow for a wallet that
 * has already been previewed, jumping directly to the wallet:ready state
 * without requiring OAuth or re-fetching balance data.
 */
export interface SilentResumeWithdrawalFlowOptions {
	/** Wallet ID to resume */
	walletId: string;

	/** Exchange this wallet is connected to */
	exchange: string;

	/** Pre-loaded balance data (if available from preview) */
	preloadedBalances?: Array<{
		asset: string;
		balance: string;
		balanceInFiat?: string;
		networks?: Array<{
			id: string;
			name: string;
			displayName: string;
			minWithdrawal: string;
			maxWithdrawal?: string;
			assetName: string;
			addressRegex?: string;
		}>;
		extra?: {
			slug?: string;
			assetId?: string;
		};
	}>;

	/** Called when wallet is not found */
	onWalletNotFound?: (walletId: string) => void;

	/** Called when wallet has invalid API credentials */
	onWalletInvalidApiCredentials?: (walletId: string) => void;

	/** Called when wallet balance is loaded */
	onWalletBalance?: (walletId: string, balances: any[]) => void;
}

export interface QuoteRequestOptions {
	asset: string;
	amount: string;
	destinationAddress: string;
	network?: string;
	tag?: string;
	includeFee?: boolean;
}

export interface LoadExchangesResult {
	success: boolean;
	error?: string;
	type?: TypeEnum2;
	result?: Array<{
		id: string;
		name: string;
		logoUrl: string;
		status: string;
	}>;
}

export interface LoadWalletResult {
	success: boolean;
	error?: string;
	type?: TypeEnum2;
	result?: {
		balances: Array<{
			asset: string;
			balance: string;
			balanceInFiat?: string;
			networks?: Array<{
				id: string;
				name: string;
				displayName: string;
				minWithdrawal: string;
				maxWithdrawal?: string;
				assetName: string;
				addressRegex?: string;
			}>;
			extra?: {
				slug?: string;
				assetId?: string;
			};
		}>;
	};
}

export interface ExecuteWithdrawalResult {
	success: boolean;
	error?: string;
	type?: TypeEnum2;
	result?: any; // The data returned from executeWithdrawalFn (workflow/transaction info)
}

export interface Submit2FAResult {
	success: boolean;
	error?: string;
	type?: TypeEnum2;
	result?: any; // The data returned from executeWithdrawalFn after 2FA submission
}

export class BluvoFlowClient {
	private webClient: BluvoWebClient;
	private flowMachine?: Machine<FlowState, FlowActionType>;
	private subscription?: Subscription | null;
	private generateId: () => string;
	private quoteRefreshTimer?: ReturnType<typeof setTimeout>;
	private qrCodeTimeoutTimer?: ReturnType<typeof setTimeout>;
	private cache: BluvoCache;

	constructor(private options: BluvoFlowClientOptions) {
		this.webClient = BluvoWebClient.createClient({
			orgId: options.orgId,
			projectId: options.projectId,
			sandbox: options.options?.sandbox,
			dev: options.options?.dev,
			customDomain: options.options?.customDomain,
		});

		this.generateId = options.mkUUIDFn || (() => crypto.randomUUID());
		this.cache = new BluvoCache(options.cache);
	}

	async loadExchanges(
		status?: Oauth2ExchangeslistexchangesResponses["200"]["exchanges"][number]["status"],
	) {
		if (!this.flowMachine) {
			// Create flow machine if it doesn't exist
			this.flowMachine = createFlowMachine({
				orgId: this.options.orgId,
				projectId: this.options.projectId,
				maxRetryAttempts: this.options.options?.maxRetryAttempts,
				autoRefreshQuotation: this.options.options?.autoRefreshQuotation,
			});
		}

		this.flowMachine.send({ type: "LOAD_EXCHANGES" });

		// Wrap in try-catch since listExchangesFn returns directly (not {data, error, success})
		let exchanges: any;
		try {
			exchanges = await this.options.listExchangesFn(status);
		} catch (err) {
			// Handle thrown exceptions
			const errorInfo = extractErrorTypeInfo(err);
			const errorMessage = err instanceof Error ? err.message : "Failed to load exchanges";
			const error = new Error(errorMessage);

			this.flowMachine.send({
				type: "EXCHANGES_FAILED",
				error,
			});

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Validate response structure
		if (!exchanges || !Array.isArray(exchanges)) {
			const errorMsg = "Failed to load exchanges: Invalid response";
			const error = new Error(errorMsg);

			this.flowMachine.send({
				type: "EXCHANGES_FAILED",
				error,
			});

			return {
				success: false,
				error: errorMsg,
			};
		}

		// Success: send to state machine and return standardized response
		this.flowMachine.send({
			type: "EXCHANGES_LOADED",
			exchanges,
		});

		return {
			// Legacy compatibility - flat array access
			exchanges,

			// New standardized structure
			success: true,
			result: exchanges,
		};
	}

	async startWithdrawalFlow(flowOptions: WithdrawalFlowOptions) {
		// Check if wallet already exists
		const { data, error, success } = await this.options.getWalletByIdFn(flowOptions.walletId);

		if (success && data?.exchange) {
			// Wallet exists, redirect to resumeWithdrawalFlow
			return this.resumeWithdrawalFlow({
				exchange: data.exchange,
				walletId: flowOptions.walletId,
			});
		}

		if (!success) {
			console.warn("Error checking wallet existence:", (error as any)?.error || (error as any)?.message || "Unknown error");
		}

		// Check if this exchange uses QR code authentication
		if (QR_CODE_EXCHANGES.includes(flowOptions.exchange.toLowerCase())) {
			return this.startQRCodeFlow(flowOptions);
		}

		// Dispose any existing flow
		this.dispose();

		// Create new flow machine
		this.flowMachine = createFlowMachine({
			orgId: this.options.orgId,
			projectId: this.options.projectId,
			maxRetryAttempts: this.options.options?.maxRetryAttempts,
			autoRefreshQuotation: this.options.options?.autoRefreshQuotation,
		});

		// Generate idempotency key for OAuth flow
		const idem = this.generateId();

		// Start OAuth flow
		this.flowMachine.send({
			type: "START_OAUTH",
			exchange: flowOptions.exchange,
			walletId: flowOptions.walletId,
			idem,
		});

		// Subscribe to workflow messages
		this.subscription = await this.webClient.listen(idem, {
			onOAuth2Complete: (message) => {
				this.flowMachine?.send({
					type: "OAUTH_COMPLETED",
					walletId: message.walletId,
					exchange: message.exchange,
				});

				// Call the onWalletConnected hook if provided
				if (this.options.onWalletConnectedFn) {
					this.options.onWalletConnectedFn(message.walletId, message.exchange);
				}

				// Auto-proceed to wallet loading
				this.loadWallet(message.walletId);
			},
			onError: (error) => {
				// Extract error code to check for specific OAuth errors
				const errorCode = extractErrorCode(error);

				// Convert error to Error instance with appropriate message
				let errorInstance: Error;
				if (error instanceof Error) {
					errorInstance = error;
				} else {
					const errorMessage =
						error && typeof error === "object" && "message" in error
							? String((error as any).message)
							: "OAuth authentication failed";
					errorInstance = new Error(errorMessage);
				}

				// OAUTH_TOKEN_EXCHANGE_FAILED is a FATAL error - wallet connection is permanently broken
				// This means the OAuth tokens are invalid/revoked/expired and the wallet needs to be reconnected
				if (errorCode === ERROR_CODES.OAUTH_TOKEN_EXCHANGE_FAILED) {
					this.flowMachine?.send({
						type: "OAUTH_FATAL",
						error: errorInstance,
					});
				}
				// Other OAuth errors are recoverable (user can retry)
				else if (
					errorCode === ERROR_CODES.OAUTH_AUTHORIZATION_FAILED ||
					errorCode === ERROR_CODES.OAUTH_INVALID_STATE
				) {
					this.flowMachine?.send({
						type: "OAUTH_FAILED",
						error: errorInstance,
					});
				} else {
					// For any other error during OAuth, treat as recoverable
					this.flowMachine?.send({
						type: "OAUTH_FAILED",
						error: errorInstance,
					});
				}
			},
		});

        // TODO: if CEX is not OAuth (i.e. binance) ask for QR-Code instead of openWindow, still we listen the same way with the websocket tho.
		// Open OAuth window
		const closeWindow = await this.webClient.oauth2.openWindow(
			flowOptions.exchange as any,
			{
				walletId: flowOptions.walletId,
				idem,
			},
			{
				onWindowClose: () => {
					// const state = this.flowMachine?.getState();
					// if (state?.type === 'oauth:processing') {
					//     this.flowMachine?.send({
					//         type: 'OAUTH_WINDOW_CLOSED_BY_USER',
					//         error: new Error('OAuth window closed by user')
					//     });
					// }
				},
			},
			flowOptions.popupOptions,
		);

		this.flowMachine.send({ type: "OAUTH_WINDOW_OPENED" });

		return {
			machine: this.flowMachine,
			closeOAuthWindow: closeWindow,
		};
	}

	/**
	 * Handle a QR code message (from WebSocket or cache replay).
	 * Extracted to share logic between startQRCodeFlow, refreshQRCode, and cache replay.
	 */
	private handleQRCodeReceived(message: QRCodeAuthWorkflowMessageBody, exchange: string, walletId: string) {
		console.log('qrcode received message:', message);
		const status = message.qrCodeStatus;

		// Backend-driven expiration (authoritative, replaces client-side timeout)
		if (status === 'expired' || status === 'used') {
			if (this.qrCodeTimeoutTimer) {
				clearTimeout(this.qrCodeTimeoutTimer);
				this.qrCodeTimeoutTimer = undefined;
			}
			// Clear cache for expired/used QR codes
			this.cache.removeCachedQRCode(exchange);
			const s = this.flowMachine?.getState();
			if (s?.type === 'qrcode:displaying' || s?.type === 'qrcode:scanning') {
				this.flowMachine?.send({ type: "QRCODE_TIMEOUT" });
			}
			return;
		}

		// Scanned → update status + trigger state transition
		if (status === 'scanned') {
			// QR code was scanned, no longer reusable — clear cache
			this.cache.removeCachedQRCode(exchange);
			this.flowMachine?.send({ type: "QRCODE_STATUS_UPDATED", qrCodeStatus: status, qrCodeExpiresAt: message.qrCodeExpiresAt || message.expiresAt });
			this.flowMachine?.send({ type: "QRCODE_SCANNED" });
			return;
		}

		// Confirmed → just update context (completion follows via onQRCodeComplete with success=true)
		if (status === 'confirmed') {
			this.cache.removeCachedQRCode(exchange);
			this.flowMachine?.send({ type: "QRCODE_STATUS_UPDATED", qrCodeStatus: status, qrCodeExpiresAt: message.qrCodeExpiresAt || message.expiresAt });
			return;
		}

		// First URL receipt or status update (available/acquired)
		const currentState = this.flowMachine?.getState();
		if (currentState?.type === 'qrcode:waiting' && message.qrCodeUrl) {
			// Clear any existing timeout
			if (this.qrCodeTimeoutTimer) {
				clearTimeout(this.qrCodeTimeoutTimer);
			}

			// Calculate timeout based on expiresAt or use default
			const expiresAt = message.qrCodeExpiresAt || message.expiresAt;
			const timeoutMs = expiresAt
				? Math.max(0, expiresAt - Date.now())
				: DEFAULT_QRCODE_TIMEOUT_MS;

			this.flowMachine?.send({
				type: "QRCODE_URL_RECEIVED",
				qrCodeUrl: message.qrCodeUrl,
				expiresAt: expiresAt || Date.now() + DEFAULT_QRCODE_TIMEOUT_MS,
			});

			// Cache the QR code for reuse on page reload
			this.cache.cacheQRCode(exchange, message);

			// Set up timeout for QR code expiration
			this.qrCodeTimeoutTimer = setTimeout(() => {
				const state = this.flowMachine?.getState();
				if (state?.type === 'qrcode:displaying' || state?.type === 'qrcode:scanning') {
					this.flowMachine?.send({ type: "QRCODE_TIMEOUT" });
				}
			}, timeoutMs);
		} else if (status && (currentState?.type === 'qrcode:displaying' || currentState?.type === 'qrcode:scanning')) {
			this.flowMachine?.send({ type: "QRCODE_STATUS_UPDATED", qrCodeStatus: status, qrCodeExpiresAt: message.qrCodeExpiresAt || message.expiresAt });
		}
	}

	/**
	 * Handle QR code completion. Clears cache and proceeds to wallet loading.
	 */
	private handleQRCodeComplete(message: { walletId: string; exchange: string }, exchange: string, walletId: string) {
		this.flowMachine?.send({
			type: "QRCODE_COMPLETED",
			walletId: message.walletId,
			exchange: message.exchange,
		});

		// Call the onWalletConnected hook if provided
		if (this.options.onWalletConnectedFn) {
			this.options.onWalletConnectedFn(message.walletId, message.exchange);
		}

		// Clear QR code timeout
		if (this.qrCodeTimeoutTimer) {
			clearTimeout(this.qrCodeTimeoutTimer);
			this.qrCodeTimeoutTimer = undefined;
		}

		// QR code flow completed — clear cache
		this.cache.removeCachedQRCode(exchange);

		// Auto-proceed to wallet loading
		this.loadWallet(message.walletId);
	}

	/**
	 * Handle QR code error. Clears timeout and sends failure action.
	 */
	private handleQRCodeError(error: Error, exchange: string, walletId: string) {
		// Clear QR code timeout
		if (this.qrCodeTimeoutTimer) {
			clearTimeout(this.qrCodeTimeoutTimer);
			this.qrCodeTimeoutTimer = undefined;
		}

		// Clear cache on error
		this.cache.removeCachedQRCode(exchange);

		this.flowMachine?.send({
			type: "QRCODE_FAILED",
			error,
		});
	}

	/**
	 * Handle generic error during QR code flow.
	 */
	private handleQRCodeFlowError(error: any, exchange: string, walletId: string) {
		// Extract error code to check for specific errors
		const errorCode = extractErrorCode(error);

		// Convert error to Error instance with appropriate message
		let errorInstance: Error;
		if (error instanceof Error) {
			errorInstance = error;
		} else {
			const errorMessage =
				error && typeof error === "object" && "message" in error
					? String((error as any).message)
					: "QR code authentication failed";
			errorInstance = new Error(errorMessage);
		}

		// Clear QR code timeout
		if (this.qrCodeTimeoutTimer) {
			clearTimeout(this.qrCodeTimeoutTimer);
			this.qrCodeTimeoutTimer = undefined;
		}

		// Clear cache on error
		this.cache.removeCachedQRCode(exchange);

		// OAUTH_TOKEN_EXCHANGE_FAILED is a FATAL error
		if (errorCode === ERROR_CODES.OAUTH_TOKEN_EXCHANGE_FAILED) {
			this.flowMachine?.send({
				type: "QRCODE_FATAL",
				error: errorInstance,
			});
		} else {
			this.flowMachine?.send({
				type: "QRCODE_FAILED",
				error: errorInstance,
			});
		}
	}

	/**
	 * Start a QR code authentication flow for exchanges that don't support OAuth popup
	 * (e.g., Binance). The flow renders a QR code for mobile app scanning.
	 *
	 * If a valid cached QR code exists (from a previous page load), it will be replayed
	 * immediately to avoid unnecessary API calls and rate-limiting.
	 */
	private async startQRCodeFlow(flowOptions: WithdrawalFlowOptions) {
		// Dispose any existing flow
		this.dispose();

		const exchange = flowOptions.exchange;

		// Always use the caller's walletId — never substitute from cache
		const walletId = flowOptions.walletId;

		// Check cache for a valid QR code before hitting the backend.
		// Only reuse cached QR display data when the walletId matches,
		// otherwise the backend pool entry belongs to a different wallet.
		const rawCachedQRCode = await this.cache.getCachedQRCode(exchange);
		const cachedQRCode = rawCachedQRCode?.walletId === walletId ? rawCachedQRCode : null;

		// Evict stale cache entry on walletId mismatch
		if (rawCachedQRCode && !cachedQRCode) {
			await this.cache.removeCachedQRCode(exchange);
		}

		// Create new flow machine
		this.flowMachine = createFlowMachine({
			orgId: this.options.orgId,
			projectId: this.options.projectId,
			maxRetryAttempts: this.options.options?.maxRetryAttempts,
			autoRefreshQuotation: this.options.options?.autoRefreshQuotation,
		});

		// Generate idempotency key for QR code flow
		const idem = this.generateId();

		// Start QR code flow
		this.flowMachine.send({
			type: "START_QRCODE",
			exchange,
			walletId,
			idem,
		});

		// Subscribe to workflow messages with QR code callbacks
		// We always subscribe even when using cache — the WebSocket will deliver
		// status updates (scanned, confirmed, completed) for the cached QR code
		this.subscription = await this.webClient.listen(idem, {
			onOAuth2Complete: (message) => {
				// This shouldn't be called for QR code flows, but handle it just in case
				this.handleQRCodeComplete(message, exchange, walletId);
			},
			onQRCodeReceived: (message) => {
				this.handleQRCodeReceived(message, exchange, walletId);
			},
			onQRCodeComplete: (message) => {
				this.handleQRCodeComplete(message, exchange, walletId);
			},
			onQRCodeError: (error) => {
				this.handleQRCodeError(error, exchange, walletId);
			},
			onError: (error) => {
				this.handleQRCodeFlowError(error, exchange, walletId);
			},
		});

		// If we have a valid cached QR code, replay it as if received from WebSocket
		if (cachedQRCode) {
			console.log('[BluvoCache] Replaying cached QR code for', exchange);
			this.handleQRCodeReceived(cachedQRCode, exchange, walletId);

			// Still need to fetch the URL and trigger the backend so the WebSocket
			// topic is active for status updates, but we already have the QR code displayed
		}

		// Fetch the QR code URL from the backend
		const { url, data, success, error } = await this.webClient.oauth2.getURL(
			exchange as any,
			{
				walletId,
				idem,
			}
		);

		if (!success || !url) {
			// If we already have a cached QR code displayed, don't fail the flow
			if (!cachedQRCode) {
				console.error("Failed to get QR code URL:", error);
				const errorMessage = typeof error === 'string'
					? error
					: (error as any)?.message || (error as any)?.error || "Failed to get QR code URL";
				this.flowMachine?.send({
					type: "QRCODE_FAILED",
					error: new Error(errorMessage),
				});
			}
		}

		if (data?.isQRCode === false) {
			// This means the exchange doesn't support QR code authentication, which is unexpected since we should only be in this flow for QR code exchanges
			const errorMessage = "Exchange does not support QR code authentication";
			console.error(errorMessage, { exchange, data });
			this.flowMachine?.send({
				type: "QRCODE_FAILED",
				error: new Error(errorMessage),
			});
			return {
				machine: this.flowMachine,
				closeOAuthWindow: null,
			};
		}

		// Trigger the backend to generate the QR code by fetching the login URL
		// The backend will then push the QR code URL via WebSocket to the topic
		// We always trigger this even with cache — the backend needs the fetch to
		// activate the WebSocket topic for status updates on the existing QR code
		if (url) {
			try {
				const response = await fetch(url, {
					method: 'GET',
					credentials: 'include',
				});
				if (!response.ok) {
					console.error("Failed to trigger QR code generation:", response.status, response.statusText);
				}
			} catch (fetchError) {
				console.error("Error triggering QR code generation:", fetchError);
				// Don't fail the flow - the WebSocket might still receive the QR code
			}
		}

		return {
			machine: this.flowMachine,
			closeOAuthWindow: null, // No OAuth window for QR code flow
		};
	}

	/**
	 * Refresh the QR code after it has expired or encountered an error.
	 * This can be called when the user is in qrcode:timeout or qrcode:error state.
	 */
	async refreshQRCode() {
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Only refresh from error or timeout states
		if (state.type !== 'qrcode:error' && state.type !== 'qrcode:timeout') {
			return {
				success: false,
				error: `Cannot refresh QR code in state: ${state.type}`,
			};
		}

		// Guard: Need exchange and wallet ID from context
		if (!state.context.exchange || !state.context.walletId) {
			return {
				success: false,
				error: "Exchange or wallet ID not found in state",
			};
		}

		const exchange = state.context.exchange!;
		const walletId = state.context.walletId!;

		// Clear cached QR code since we're explicitly refreshing
		await this.cache.removeCachedQRCode(exchange);

		// Send refresh action to transition back to waiting state
		this.flowMachine.send({ type: "REFRESH_QRCODE" });

		// Generate new idempotency key
		const idem = this.generateId();

		// Update topic name in context (we need to resubscribe)
		if (this.subscription) {
			await this.webClient.unsubscribe(this.subscription.topicName);
		}

		// Re-subscribe with new idem using shared handlers
		this.subscription = await this.webClient.listen(idem, {
			onOAuth2Complete: (message) => {
				this.handleQRCodeComplete(message, exchange, walletId);
			},
			onQRCodeReceived: (message: any) => {
				this.handleQRCodeReceived(message, exchange, walletId);
			},
			onQRCodeComplete: (message: any) => {
				this.handleQRCodeComplete(message, exchange, walletId);
			},
			onQRCodeError: (error: any) => {
				this.handleQRCodeError(error, exchange, walletId);
			},
			onError: (error) => {
				this.handleQRCodeFlowError(error, exchange, walletId);
			},
		});

		// Fetch new QR code URL
		const { url, success, error } = await this.webClient.oauth2.getURL(
			state.context.exchange as any,
			{
				walletId: state.context.walletId,
				idem,
			}
		);

		if (!success || !url) {
			console.error("Failed to refresh QR code URL:", error);
			const errorMessage = typeof error === 'string'
				? error
				: (error as any)?.message || (error as any)?.error || "Failed to refresh QR code URL";
			this.flowMachine?.send({
				type: "QRCODE_FAILED",
				error: new Error(errorMessage),
			});
			return {
				success: false,
				error: errorMessage,
			};
		}

		// Trigger the backend to generate the new QR code by fetching the login URL
		try {
			const response = await fetch(url, {
				method: 'GET',
				credentials: 'include',
			});
			if (!response.ok) {
				console.error("Failed to trigger QR code refresh:", response.status, response.statusText);
			}
		} catch (fetchError) {
			console.error("Error triggering QR code refresh:", fetchError);
			// Don't fail the flow - the WebSocket might still receive the QR code
		}

		return {
			success: true,
		};
	}

	async resumeWithdrawalFlow(flowOptions: ResumeWithdrawalFlowOptions) {
		// Dispose any existing flow
		this.dispose();

		// Create new flow machine
		this.flowMachine = createFlowMachine({
			orgId: this.options.orgId,
			projectId: this.options.projectId,
			maxRetryAttempts: this.options.options?.maxRetryAttempts,
			autoRefreshQuotation: this.options.options?.autoRefreshQuotation,
		});

		// We need to transition through the states properly
		// First, start OAuth flow to set up context
		this.flowMachine.send({
			type: "START_OAUTH",
			exchange: flowOptions.exchange,
			walletId: flowOptions.walletId,
			idem: this.generateId(),
		});

		// Then mark OAuth as completed
		this.flowMachine.send({
			type: "OAUTH_WINDOW_OPENED",
		});

		// Complete OAuth
		this.flowMachine.send({
			type: "OAUTH_COMPLETED",
			walletId: flowOptions.walletId,
			exchange: flowOptions.exchange,
		});

		// Call the onWalletConnected hook if provided
		if (this.options.onWalletConnectedFn) {
			this.options.onWalletConnectedFn(
				flowOptions.walletId,
				flowOptions.exchange,
			);
		}

		// Load wallet immediately
		this.loadWallet(flowOptions.walletId);

		return {
			machine: this.flowMachine,
            closeOAuthWindow: null
		};
	}

	/**
	 * Silently resume a withdrawal flow with optional preloaded balance data
	 *
	 * This method allows you to resume a withdrawal flow for a wallet that has already
	 * been previewed, jumping directly to the wallet:ready state. If balance data is
	 * preloaded (from a preview), it will be used directly. Otherwise, the method will
	 * ping the wallet and fetch the balance data.
	 *
	 * This is useful for scenarios where users select a wallet from a preview list and
	 * want to continue directly to the withdrawal interface without re-authenticating.
	 *
	 * @param options Configuration for silent resume
	 * @returns Object containing the flow machine instance
	 */
	async silentResumeWithdrawalFlow(options: SilentResumeWithdrawalFlowOptions) {
		// Dispose any existing flow
		this.dispose();

		// Create new flow machine
		this.flowMachine = createFlowMachine({
			orgId: this.options.orgId,
			projectId: this.options.projectId,
			maxRetryAttempts: this.options.options?.maxRetryAttempts,
			autoRefreshQuotation: this.options.options?.autoRefreshQuotation,
		});

		let balances = options.preloadedBalances;

		// If no preloaded balances, fetch them
		if (!balances) {
			// Step 1: Ping wallet to validate
			const {
				data: pingResult,
				error: pingError,
				success: pingSuccess
			} = await this.options.pingWalletByIdFn(
				options.walletId,
			);

			// Check for invalid credentials
			if (!pingSuccess) {
				const errorCode = extractErrorCode(pingError);
				if (errorCode === ERROR_CODES.WALLET_NOT_FOUND) {
					options.onWalletNotFound?.(options.walletId);
				}
				// Silent failure - just log and return
				console.warn("Failed to ping wallet:", (pingError as any)?.error || (pingError as any)?.message || "Failed to ping wallet");
				return;
			}

			if (pingResult?.status === "INVALID_API_CREDENTIALS") {
				options.onWalletInvalidApiCredentials?.(options.walletId);
				// Silent failure - just log and return
				console.warn("Invalid API credentials for wallet");
				return;
			}

			// Step 2: Fetch withdrawable balance
			const {
				data: balanceResponse,
				error: balanceError,
				success: balanceSuccess,
			} = await this.options.fetchWithdrawableBalanceFn(
				options.walletId,
			);

			if (!balanceSuccess) {
				const errorCode = extractErrorCode(balanceError);
				if (errorCode === ERROR_CODES.WALLET_NOT_FOUND) {
					options.onWalletNotFound?.(options.walletId);
				}
				// Silent failure - just log and return
				console.warn("Failed to fetch withdrawable balance:", (balanceError as any)?.error || (balanceError as any)?.message || "Failed to fetch withdrawable balance");
				return;
			}

			// Transform to expected format
			balances = balanceResponse?.balances ? transformBalances(balanceResponse.balances) : [];

			// Call success callback
			options.onWalletBalance?.(options.walletId, balances);
		}

		// Transition through states to reach wallet:ready
		// This ensures the state machine is in the correct state

		// 1. Start OAuth (to set up context)
		this.flowMachine.send({
			type: "START_OAUTH",
			exchange: options.exchange,
			walletId: options.walletId,
			idem: this.generateId(),
		});

		// 2. Mark OAuth window as opened
		this.flowMachine.send({
			type: "OAUTH_WINDOW_OPENED",
		});

		// 3. Complete OAuth
		this.flowMachine.send({
			type: "OAUTH_COMPLETED",
			walletId: options.walletId,
			exchange: options.exchange,
		});

		// 4. Load wallet (transition to wallet:loading)
		this.flowMachine.send({
			type: "LOAD_WALLET",
		});

		// 5. Set wallet as loaded with preloaded/fetched balances (transition to wallet:ready)
		this.flowMachine.send({
			type: "WALLET_LOADED",
			balances: balances as any, // Type assertion to satisfy TypeScript
		});

		return {
			machine: this.flowMachine,
		};
	}

	private async loadWallet(walletId: string) {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		this.flowMachine.send({ type: "LOAD_WALLET" });

		const { data: withdrawableBalanceInfo, error, success } = await this.options
			.fetchWithdrawableBalanceFn(walletId);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to load wallet";
			const flowError = new Error(errorMessage);

			this.flowMachine.send({
				type: "WALLET_FAILED",
				error: flowError,
			});

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Validate response structure
		if (!withdrawableBalanceInfo?.balances) {
			const errorMsg = "No balance data returned";
			const flowError = new Error(errorMsg);

			this.flowMachine.send({
				type: "WALLET_FAILED",
				error: flowError,
			});

			return {
				success: false,
				error: errorMsg,
			};
		}

		// Transform balances
		const transformedBalances = transformBalances(withdrawableBalanceInfo.balances);

		this.flowMachine.send({
			type: "WALLET_LOADED",
			balances: transformedBalances,
		});

		return {
			// Legacy compatibility - flat balances field
			balances: transformedBalances,

			// New standardized structure
			success: true,
			result: {
				balances: transformedBalances,
			},
		};
	}

	async requestQuote(options: QuoteRequestOptions) {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: No wallet ID
		if (!state.context.walletId) {
			return {
				success: false,
				error: "Wallet ID not found in state",
			};
		}

		console.log(
			"[SDK] requestQuote called, current state:",
			state.type,
			"amount:",
			options.amount,
		);

		this.flowMachine.send({
			type: "REQUEST_QUOTE",
			asset: options.asset,
			amount: options.amount,
			destinationAddress: options.destinationAddress,
			network: options.network,
		});

		console.log("[SDK] REQUEST_QUOTE action sent to state machine");

		const {
			data: quote,
			error,
			success
		} = await this.options.requestQuotationFn(
			state.context.walletId,
			{
				asset: options.asset,
				amount: options.amount,
				address: options.destinationAddress,
				network: options.network,
				tag: options.tag,
				includeFee: options.includeFee ?? true,
			},
		);

		if (!success) {
			// Extract comprehensive error type information
			const errorInfo = extractErrorTypeInfo(error);
			let flowError: Error;

			// Handle known error codes with specific messages
			if (errorInfo.knownCode) {
				switch (errorInfo.knownCode) {
					case ERROR_CODES.WITHDRAWAL_INSUFFICIENT_BALANCE:
					case ERROR_CODES.WITHDRAWAL_INSUFFICIENT_BALANCE_FOR_FEE:
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.INSUFFICIENT_BALANCE: // Legacy compatibility
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.INSUFFICIENT_BALANCE_CANNOT_COVER_FEE: // Legacy compatibility
						flowError = new Error("Insufficient balance");
						break;
					case ERROR_CODES.WITHDRAWAL_AMOUNT_BELOW_MINIMUM:
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.AMOUNT_BELOW_MINIMUM: // Legacy compatibility
						flowError = new Error("Amount below minimum");
						break;
					case ERROR_CODES.WITHDRAWAL_AMOUNT_ABOVE_MAXIMUM:
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.AMOUNT_ABOVE_MAXIMUM: // Legacy compatibility
						flowError = new Error("Amount above maximum");
						break;
					case ERROR_CODES.WITHDRAWAL_INVALID_ADDRESS:
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.INVALID_ADDRESS: // Legacy compatibility
						flowError = new Error("Invalid destination address");
						break;
					case ERROR_CODES.WITHDRAWAL_NETWORK_NOT_SUPPORTED:
					case WITHDRAWAL_QUOTATION_ERROR_TYPES.NETWORK_NOT_SUPPORTED: // Legacy compatibility
						flowError = new Error("Network not supported");
						break;
					default:
						flowError = error instanceof Error ? error : new Error((error as any)?.error || (error as any)?.message || "Failed to get quote");
				}
			} else {
				// No known error code, use generic error message
				flowError = error instanceof Error ? error : new Error((error as any)?.error || (error as any)?.message || "Failed to get quote");
			}

			this.flowMachine.send({
				type: "QUOTE_FAILED",
				error: flowError,
			});

			return {
				success: false,
				error: flowError.message,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		if (!quote) {
			const errorMsg = "No quote returned from backend";
			this.flowMachine.send({
				type: "QUOTE_FAILED",
				error: new Error(errorMsg),
			});

			return {
				success: false,
				error: errorMsg,
			};
		}

		console.log(
			"[SDK] Backend returned quote with ID:",
			quote.id,
			"ExpiresAt:",
			quote.expiresAt,
		);

		const quoteData = {
			id: quote.id,
			asset: quote.asset,
			amount: String(quote.amountNoFee),
			estimatedFee: String(quote.estimatedFee),
			estimatedTotal: String(quote.estimatedTotal),

			amountWithFeeInFiat: String(quote.amountWithFeeInFiat),
			amountNoFeeInFiat: String(quote.amountNoFeeInFiat),
			estimatedFeeInFiat: String(quote.estimatedFeeInFiat),

			additionalInfo: quote.additionalInfo,

			expiresAt: new Date(quote.expiresAt).getTime(),
		};

		console.log(
			"[SDK] Sending QUOTE_RECEIVED action with new quote ID:",
			quoteData.id,
			"ExpiresAt:",
			new Date(quoteData.expiresAt).toLocaleTimeString(),
		);

		this.flowMachine.send({
			type: "QUOTE_RECEIVED",
			quote: quoteData,
		});

		// Clear any existing quote refresh timer
		if (this.quoteRefreshTimer) {
			clearTimeout(this.quoteRefreshTimer);
		}

		// Set up quote expiration timer
		const expiresIn = new Date(quote.expiresAt).getTime() - Date.now();
		if (expiresIn > 0) {
			this.quoteRefreshTimer = setTimeout(() => {
				const currentState = this.flowMachine?.getState();
				if (
					currentState?.type === "quote:ready" &&
					currentState.context.quote?.id === quote.id
				) {
					// Check if auto-refresh is enabled
					const autoRefresh =
						currentState.context.autoRefreshQuotation !== undefined
							? currentState.context.autoRefreshQuotation
							: true; // Default to true

					if (autoRefresh && currentState.context.lastQuoteRequest) {
						// Auto-refresh the quote
						console.log("[SDK] Quote expired, auto-refreshing...");
						this.requestQuote(currentState.context.lastQuoteRequest);
					} else {
						// No auto-refresh, transition to expired state
						console.log(
							"[SDK] Quote expired, transitioning to expired state",
						);
						this.flowMachine?.send({ type: "QUOTE_EXPIRED" });
					}
				}
			}, expiresIn);
		}

		// Return success with quote data
		return {
            // legacy compatibility:
            // contract
            rawQuote: quote,
            quoteData,

            // expand
			success: true,
			result: {
				rawQuote: quote,
				quoteData,
			},
		};
	}

	async executeWithdrawal(quoteId: string) {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: No wallet ID or wrong state
		if (!state.context.walletId) {
			return {
				success: false,
				error: "Wallet ID not found in state",
			};
		}

		if (state.type !== "quote:ready") {
			return {
				success: false,
				error: `Cannot execute withdrawal in state: ${state.type}`,
			};
		}

		// Start withdrawal process
		this.flowMachine.send({
			type: "START_WITHDRAWAL",
			quoteId,
		});

		if (this.subscription) {
			await this.webClient.unsubscribe(this.subscription.topicName);
		}

		this.subscription = await this.webClient.listen(quoteId, {
			onWithdrawComplete: (message: any) => {
				console.log("onWithdrawComplete received:", message);

				// {
				//     "type": "withdraw",
				//     "success": true,
				//     "walletId": "a57f3fd3-0660-438c-8e4d-e781b414acfa",
				//     "step": "complete",
				//     "stepIndex": 4,
				//     "totalSteps": 4
				// }

				if (message.success && message.type === WorkflowTypes.WithdrawFunds) {
					console.log(
						"Withdrawal completed successfully, transitioning state machine",
					);
					console.log(
						"Current flow machine state before completion:",
						this.flowMachine?.getState()?.type,
					);

					// First, send success to update the withdrawal machine state
					this.flowMachine?.send({
						type: "WITHDRAWAL_SUCCESS",
						transactionId:
							message.transactionId || message.walletId || "completed",
					});

					// Then send completion action
					this.flowMachine?.send({
						type: "WITHDRAWAL_COMPLETED",
						transactionId:
							message.transactionId || message.walletId || "completed",
					});

					// Log current state after sending actions
					const currentState = this.flowMachine?.getState();
					console.log(
						"Flow machine state after completion actions:",
						currentState?.type,
					);
				}
			},
			onStep: (message: WorkflowMessageBody) => {
				if (message.type === WorkflowTypes.WithdrawFunds) {
					const withdrawMessage = message as WithdrawFundsWorkflowMessageBody;
					this.flowMachine?.send({
						type: "WITHDRAWAL_PROGRESS",
						message: withdrawMessage.step,
					});
				}
			},
			onError: (error) => {
				const errorCode = extractErrorCode(error);

				// Handle new error codes
				switch (errorCode) {
					case ERROR_CODES.WITHDRAWAL_2FA_REQUIRED_TOTP:
						this.flowMachine?.send({ type: "WITHDRAWAL_REQUIRES_2FA" });
						return;

					case ERROR_CODES.WITHDRAWAL_2FA_REQUIRED_SMS:
						this.flowMachine?.send({ type: "WITHDRAWAL_REQUIRES_SMS" });
						return;

					case ERROR_CODES.WITHDRAWAL_2FA_INVALID:
						this.flowMachine?.send({ type: "WITHDRAWAL_2FA_INVALID" });
						return;

					case ERROR_CODES.WITHDRAWAL_KYC_REQUIRED:
						this.flowMachine?.send({ type: "WITHDRAWAL_REQUIRES_KYC" });
						return;

					case ERROR_CODES.WITHDRAWAL_INSUFFICIENT_BALANCE:
						this.flowMachine?.send({ type: "WITHDRAWAL_INSUFFICIENT_BALANCE" });
						return;

					case ERROR_CODES.WITHDRAWAL_INVALID_ADDRESS:
						this.flowMachine?.send({
							type: "WITHDRAWAL_FATAL",
							error: new Error("Invalid destination address"),
						});
						return;

					case ERROR_CODES.WITHDRAWAL_AMOUNT_BELOW_MINIMUM:
						this.flowMachine?.send({
							type: "WITHDRAWAL_FATAL",
							error: new Error("Amount below minimum"),
						});
						return;

					case ERROR_CODES.QUOTE_EXPIRED:
						this.flowMachine?.send({ type: "QUOTE_EXPIRED" });
						return;

					case ERROR_CODES.WITHDRAWAL_2FA_METHOD_NOT_SUPPORTED:
						this.flowMachine?.send({
							type: "WITHDRAWAL_2FA_METHOD_NOT_SUPPORTED",
							result: extractErrorResult(error) as { valid2FAMethods?: string[] } | undefined,
						});
						return;

					case ERROR_CODES.WITHDRAWAL_2FA_REQUIRED_MULTI_STEPS:
						this.flowMachine?.send({
							type: "WITHDRAWAL_REQUIRES_2FA_MULTI_STEPS",
							result: extractErrorResult(error) as {
								bizNo: string;
								steps: Array<{
									type: 'GOOGLE' | 'EMAIL' | 'FACE' | 'SMS';
									status: 'pending' | 'success' | 'failed';
									required: boolean;
									metadata?: {
										email?: string;
										emailSent?: boolean;
										qrCodeUrl?: string;
										qrCodeValidSeconds?: number;
									};
								}>;
								relation: 'AND' | 'OR';
								mfa?: {
									verified: {
										GOOGLE?: boolean;
										EMAIL?: boolean;
										FACE?: boolean;
										SMS?: boolean;
									};
								};
							},
						});
						return;

					case ERROR_CODES.WITHDRAWAL_2FA_INCOMPLETE:
						this.flowMachine?.send({
							type: "WITHDRAWAL_2FA_INCOMPLETE",
							result: extractErrorResult(error) as {
								bizNo: string;
								steps: Array<{
									type: 'GOOGLE' | 'EMAIL' | 'FACE' | 'SMS';
									status: 'pending' | 'success' | 'failed';
									required: boolean;
									metadata?: {
										email?: string;
										emailSent?: boolean;
										qrCodeUrl?: string;
										qrCodeValidSeconds?: number;
									};
								}>;
								relation: 'AND' | 'OR';
								mfa?: {
									verified: {
										GOOGLE?: boolean;
										EMAIL?: boolean;
										FACE?: boolean;
										SMS?: boolean;
									};
								};
							},
						});
						return;

					case ERROR_CODES.WITHDRAWAL_DRY_RUN_COMPLETE:
						this.flowMachine?.send({
							type: "WITHDRAWAL_DRY_RUN_COMPLETE",
							result: extractErrorResult(error) as {
								bizNo: string;
								steps: Array<{
									type: 'GOOGLE' | 'EMAIL' | 'FACE' | 'SMS';
									status: 'pending' | 'success' | 'failed';
									required: boolean;
									metadata?: {
										email?: string;
										emailSent?: boolean;
										qrCodeUrl?: string;
										qrCodeValidSeconds?: number;
									};
								}>;
								relation: 'AND' | 'OR';
								mfa?: {
									verified: {
										GOOGLE?: boolean;
										EMAIL?: boolean;
										FACE?: boolean;
										SMS?: boolean;
									};
								};
							},
						});
						return;
				}

				console.error("Unhandled withdrawal error", error);

				// Send WITHDRAWAL_FATAL to trigger the fatal state transition
				this.flowMachine?.send({
					type: "WITHDRAWAL_FATAL",
					error: error,
				});
			},
		});

		// Execute withdrawal
		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			quoteId,
			quoteId,
			{},
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to execute withdrawal";

			console.error("executeWithdrawal error", error);
			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		// Return standardized success response
		return {
			// Legacy compatibility - direct access to response data
			...(res??{}),

			// New standardized structure
			success: true,
			result: res,
		};
	}

	async submit2FA(code: string) {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Wrong state
		if (state.type !== "withdraw:error2FA") {
			return {
				success: false,
				error: `Cannot submit 2FA in state: ${state.type}`,
			};
		}

		// Guard: No quote or wallet ID
		if (!state.context.quote || !state.context.walletId) {
			return {
				success: false,
				error: "Quote or wallet ID not found in state",
			};
		}

		this.flowMachine.send({
			type: "SUBMIT_2FA",
			code,
		});

		// Re-execute withdrawal with 2FA code
		const quote = state.context.quote;
		const idem = this.generateId();

		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			idem,
			quote.id,
			{ twofa: code },
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to submit 2FA code";

			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		// Return standardized success response
		return {
			// Legacy compatibility - direct access to response data
			...(res ?? {}),

			// New standardized structure
			success: true,
			result: res,
		};
	}

	async submitSMS(code: string) {
		if (!this.flowMachine) return;

		const state = this.flowMachine.getState();
		if (state.type !== "withdraw:errorSMS") return;

		this.flowMachine.send({
			type: "SUBMIT_SMS",
			code,
		});

		// Re-execute withdrawal with SMS code
		const quote = state.context.quote;
		if (quote && state.context.walletId) {
			const idem = this.generateId();

			const { data: res, error, success } = await this.options.executeWithdrawalFn(
				state.context.walletId,
				idem,
				quote.id,
				{},
			);

			if (!success) {
				this.handleWithdrawalError(error);
				return;
			}

			// Withdrawal is in progress, will be handled by WebSocket callbacks
			return res;
		}
	}

	async retryWithdrawal() {
		if (!this.flowMachine) return;

		const state = this.flowMachine.getState();
		if (state.type !== "withdraw:retrying") return;

		this.flowMachine.send({ type: "RETRY_WITHDRAWAL" });

		// Re-execute withdrawal with new idempotency key
		const quote = state.context.quote;
		if (quote) {
			return await this.executeWithdrawal(quote.id);
		}
	}

	/**
	 * Submit a verification code for a specific step in multi-step 2FA flow.
	 * This stores the code and re-invokes the withdrawal with all collected codes.
	 *
	 * @param stepType The type of step to submit code for (GOOGLE, EMAIL, or SMS)
	 * @param code The verification code
	 */
	async submit2FAMultiStep(stepType: 'GOOGLE' | 'EMAIL' | 'SMS', code: string) {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Wrong state
		if (state.type !== "withdraw:error2FAMultiStep") {
			return {
				success: false,
				error: `Cannot submit multi-step 2FA in state: ${state.type}`,
			};
		}

		// Guard: No quote, wallet ID, or multiStep2FA context
		if (!state.context.quote || !state.context.walletId || !state.context.multiStep2FA) {
			return {
				success: false,
				error: "Quote, wallet ID, or multi-step 2FA context not found",
			};
		}

		// Send action to store the code and transition to processing
		this.flowMachine.send({
			type: "SUBMIT_2FA_MULTI_STEP",
			stepType,
			code,
		});

		// Build the params with all collected codes plus the new one
		const multiStep2FA = state.context.multiStep2FA;
		const collectedCodes = { ...multiStep2FA.collectedCodes };

		// Add the new code
		if (stepType === 'GOOGLE') collectedCodes.twofa = code;
		else if (stepType === 'EMAIL') collectedCodes.emailCode = code;
		else if (stepType === 'SMS') collectedCodes.smsCode = code;

		// Re-execute withdrawal with bizNo + all collected codes
		const quote = state.context.quote;
		const idem = this.generateId();

		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			idem,
			quote.id,
			{
				bizNo: multiStep2FA.bizNo,
				...collectedCodes,
			},
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to submit multi-step 2FA";

			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		return {
			success: true,
			result: res,
		};
	}

	/**
	 * Confirm the withdrawal after all multi-step 2FA steps are verified.
	 * This executes the withdrawal without dryRun flag.
	 */
	async confirmWithdrawal() {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Wrong state
		if (state.type !== "withdraw:readyToConfirm") {
			return {
				success: false,
				error: `Cannot confirm withdrawal in state: ${state.type}`,
			};
		}

		// Guard: No quote, wallet ID, or multiStep2FA context
		if (!state.context.quote || !state.context.walletId || !state.context.multiStep2FA) {
			return {
				success: false,
				error: "Quote, wallet ID, or multi-step 2FA context not found",
			};
		}

		// Send action to transition to processing
		this.flowMachine.send({ type: "CONFIRM_WITHDRAWAL" });

		// Execute withdrawal with bizNo + all collected codes (WITHOUT dryRun)
		const multiStep2FA = state.context.multiStep2FA;
		const quote = state.context.quote;
		const idem = this.generateId();

		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			idem,
			quote.id,
			{
				bizNo: multiStep2FA.bizNo,
				...multiStep2FA.collectedCodes,
				// No dryRun flag - this is the real execution
			},
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to confirm withdrawal";

			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		return {
			success: true,
			result: res,
		};
	}

	/**
	 * Poll for FACE verification completion in multi-step 2FA flow.
	 * This re-invokes the withdrawal with just the bizNo to check if FACE has been verified.
	 * NOTE: Does NOT transition state to processing — keeps the MFA UI visible during polling.
	 */
	async pollFaceVerification() {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Wrong state
		if (state.type !== "withdraw:error2FAMultiStep") {
			return {
				success: false,
				error: `Cannot poll FACE verification in state: ${state.type}`,
			};
		}

		// Guard: No quote, wallet ID, or multiStep2FA context
		if (!state.context.quote || !state.context.walletId || !state.context.multiStep2FA) {
			return {
				success: false,
				error: "Quote, wallet ID, or multi-step 2FA context not found",
			};
		}

		// NOTE: We intentionally do NOT send POLL_FACE_VERIFICATION to the state machine here.
		// Transitioning to withdraw:processing would hide the MFA UI during polling.
		// The API response will be handled by the error handler which dispatches
		// WITHDRAWAL_2FA_INCOMPLETE or WITHDRAWAL_DRY_RUN_COMPLETE back to the state machine.

		// Re-execute withdrawal with bizNo only (no collectedCodes to avoid re-triggering other steps)
		const multiStep2FA = state.context.multiStep2FA;
		const quote = state.context.quote;
		const idem = this.generateId();

		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			idem,
			quote.id,
			{
				bizNo: multiStep2FA.bizNo,
				params: {
					dryRun: true, // Indicate this is a polling attempt, not a new submission
				}
			},
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to poll FACE verification";

			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		return {
			success: true,
			result: res,
		};
	}

	/**
	 * Poll for ROAMING_FIDO verification completion in multi-step 2FA flow.
	 * This re-invokes the withdrawal with just the bizNo to check if ROAMING_FIDO has been verified.
	 * NOTE: Does NOT transition state to processing — keeps the MFA UI visible during polling.
	 */
	async pollRoamingFidoVerification() {
		// Guard: No flow machine
		if (!this.flowMachine) {
			return {
				success: false,
				error: "Flow machine not initialized",
			};
		}

		const state = this.flowMachine.getState();

		// Guard: Wrong state
		if (state.type !== "withdraw:error2FAMultiStep") {
			return {
				success: false,
				error: `Cannot poll ROAMING_FIDO verification in state: ${state.type}`,
			};
		}

		// Guard: No quote, wallet ID, or multiStep2FA context
		if (!state.context.quote || !state.context.walletId || !state.context.multiStep2FA) {
			return {
				success: false,
				error: "Quote, wallet ID, or multi-step 2FA context not found",
			};
		}

		// NOTE: We intentionally do NOT send a state transition action here.
		// Transitioning to withdraw:processing would hide the MFA UI during polling.
		// The API response will be handled by the error handler which dispatches
		// WITHDRAWAL_2FA_INCOMPLETE or WITHDRAWAL_DRY_RUN_COMPLETE back to the state machine.

		// Re-execute withdrawal with bizNo only (no collectedCodes to avoid re-triggering other steps)
		const multiStep2FA = state.context.multiStep2FA;
		const quote = state.context.quote;
		const idem = this.generateId();

		const { data: res, error, success } = await this.options.executeWithdrawalFn(
			state.context.walletId,
			idem,
			quote.id,
			{
				bizNo: multiStep2FA.bizNo,
				params: {
					dryRun: true, // Indicate this is a polling attempt, not a new submission
				}
			},
		);

		if (!success) {
			// Extract error type information
			const errorInfo = extractErrorTypeInfo(error);
			const errorMessage = error instanceof Error
				? error.message
				: (error as any)?.error || (error as any)?.message || "Failed to poll ROAMING_FIDO verification";

			this.handleWithdrawalError(error);

			return {
				success: false,
				error: errorMessage,
				type: (errorInfo.rawType as TypeEnum2) || undefined,
			};
		}

		// Withdrawal is in progress, will be handled by WebSocket callbacks
		return {
			success: true,
			result: res,
		};
	}

	private handleWithdrawalError(error: BluvoError) {
		// Use centralized error handler
		WithdrawalErrorHandler.handleWithdrawalError(error, this.flowMachine);
	}

	getState() {
		return this.flowMachine?.getState();
	}

	subscribe(listener: (state: FlowState) => void) {
		return this.flowMachine?.subscribe(listener) || (() => {});
	}

	cancel() {
		this.flowMachine?.send({ type: "CANCEL_FLOW" });
		this.dispose();
	}

	dispose() {
		if (this.quoteRefreshTimer) {
			clearTimeout(this.quoteRefreshTimer);
			this.quoteRefreshTimer = undefined;
		}

		if (this.qrCodeTimeoutTimer) {
			clearTimeout(this.qrCodeTimeoutTimer);
			this.qrCodeTimeoutTimer = undefined;
		}

		if (this.subscription) {
			this.webClient.unsubscribe(this.subscription.topicName);
			this.subscription = undefined;
		}

		if (this.flowMachine) {
			this.flowMachine.dispose();
			this.flowMachine = undefined;
		}
	}

	// TEST METHOD - For testing withdrawal completion without real transactions
	testWithdrawalComplete(transactionId?: string) {
		console.log("🧪 TEST: Simulating withdrawal completion");

		// Simulate the same message that would come from onWithdrawComplete
		const mockMessage = {
			type: "withdraw",
			success: true,
			walletId: "test-wallet-id-" + Date.now(),
			step: "complete",
			stepIndex: 4,
			totalSteps: 4,
			transactionId: transactionId || "test-transaction-" + Date.now(),
		};

		// Execute the same logic as in onWithdrawComplete
		if (mockMessage.success && mockMessage.type === "withdraw") {
			console.log(
				"🧪 TEST: Withdrawal completed successfully, transitioning state machine",
			);
			console.log(
				"🧪 TEST: Current flow machine state before completion:",
				this.flowMachine?.getState()?.type,
			);

			// First, send success to update the withdrawal machine state
			this.flowMachine?.send({
				type: "WITHDRAWAL_SUCCESS",
				transactionId: mockMessage.transactionId,
			});

			// Then send completion action
			this.flowMachine?.send({
				type: "WITHDRAWAL_COMPLETED",
				transactionId: mockMessage.transactionId,
			});

			// Log current state after sending actions
			const currentState = this.flowMachine?.getState();
			console.log(
				"🧪 TEST: Flow machine state after completion actions:",
				currentState?.type,
			);
		}
	}
}
